import api from '../api';
import { getAuthHeaders } from '../hooks/useAuthHeaders';
import { AGENT_DISPLAY_NAME } from '../constants/agentIdentity';

export const chatWithAgent = async (payload = {}, options = {}) => {
  const config = {
    ...getAuthHeaders(),
    ...(options.signal ? { signal: options.signal } : {})
  };
  const res = await api.post('/api/agent/chat', payload, config);
  return res.data || {};
};

const apiUrl = (path = '') => {
  const base = String(api.defaults?.baseURL || '').trim();
  if (!base) return path;
  if (/^https?:\/\//i.test(path)) return path;
  return `${base.replace(/\/+$/g, '')}/${String(path || '').replace(/^\/+/g, '')}`;
};

const parseSseBlock = (block = '') => {
  let event = 'message';
  const data = [];
  String(block || '').split(/\r?\n/).forEach((line) => {
    if (line.startsWith('event:')) event = line.slice(6).trim() || 'message';
    if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
  });
  const raw = data.join('\n');
  if (!raw) return { event, payload: null };
  try {
    return { event, payload: JSON.parse(raw) };
  } catch (_error) {
    return { event, payload: { raw } };
  }
};

export const AGENT_STREAM_IDLE_TIMEOUT_MS = 30 * 1000;

const readStreamWithTimeout = (reader, timeoutMs) => {
  const safeTimeoutMs = Math.max(100, Number(timeoutMs) || AGENT_STREAM_IDLE_TIMEOUT_MS);
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error(`${AGENT_DISPLAY_NAME} took too long to answer, so nothing changed. Ask again.`);
      error.code = 'AGENT_STREAM_TIMEOUT';
      reject(error);
    }, safeTimeoutMs);
  });
  return Promise.race([reader.read(), timeout]).finally(() => clearTimeout(timeoutId));
};

export const streamChatWithAgent = async (payload = {}, handlers = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(apiUrl('/api/agent/chat/stream'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload || {}),
    signal: handlers.signal
  });

  if (!res.ok) {
    let message = 'Failed to generate agent reply.';
    try {
      const body = await res.json();
      message = body?.error || message;
    } catch (_error) {
      // Keep the generic error for non-JSON stream failures.
    }
    throw new Error(message);
  }

  if (!res.body?.getReader) {
    const body = await res.json();
    handlers.onFinal?.(body);
    return body || {};
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalPayload = null;
  let streamError = null;
  const idleTimeoutMs = handlers.idleTimeoutMs || AGENT_STREAM_IDLE_TIMEOUT_MS;

  const consumeBlock = (block) => {
    const { event, payload: blockPayload } = parseSseBlock(block);
    if (!blockPayload) return;
    handlers.onEvent?.(event, blockPayload);
    if (event === 'agent-activity') handlers.onActivity?.(blockPayload);
    if (event === 'agent-delta') handlers.onDelta?.(String(blockPayload.delta || ''), blockPayload);
    if (event === 'agent-final') {
      finalPayload = blockPayload;
      handlers.onFinal?.(blockPayload);
    }
    if (event === 'error') {
      streamError = new Error(blockPayload.error || blockPayload.message || 'Failed to generate agent reply.');
    }
  };

  try {
    while (true) {
      const { done, value } = await readStreamWithTimeout(reader, idleTimeoutMs);
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split(/\r?\n\r?\n/);
      buffer = blocks.pop() || '';
      blocks.forEach(consumeBlock);
    }
  } catch (error) {
    if (error?.code === 'AGENT_STREAM_TIMEOUT') {
      try {
        await reader.cancel?.();
      } catch (_cancelError) {
        // The useful outcome is already the recoverable timeout shown to the user.
      }
    }
    throw error;
  }
  buffer += decoder.decode();
  if (buffer.trim()) consumeBlock(buffer);
  if (streamError) throw streamError;
  return finalPayload || {};
};

export const getAgentEntitlements = async () => {
  const res = await api.get('/api/agent/entitlements', getAuthHeaders());
  return res.data || { entitlements: {} };
};

export const getAgentConnectApprovalSession = async (sessionId) => {
  const safeSessionId = encodeURIComponent(String(sessionId || '').trim());
  const res = await api.get(`/api/agent-connect/sessions/${safeSessionId}/approval`, { skipAuthHandling: true });
  return res.data || {};
};

export const approveAgentConnectSession = async (sessionId, payload = {}) => {
  const safeSessionId = encodeURIComponent(String(sessionId || '').trim());
  const res = await api.post(`/api/agent-connect/sessions/${safeSessionId}/approve`, payload, getAuthHeaders());
  return res.data || {};
};

export const createAgentTaskLink = async (payload = {}) => {
  const res = await api.post('/api/agent-task-links', payload, getAuthHeaders());
  return res.data || {};
};

export const getAgentTaskLink = async (taskId) => {
  const safeTaskId = encodeURIComponent(String(taskId || '').trim());
  const res = await api.get(`/api/agent-task-links/${safeTaskId}`, { skipAuthHandling: true });
  return res.data || {};
};

export const dispatchAgentTaskLink = async (taskId, payload = {}) => {
  const safeTaskId = encodeURIComponent(String(taskId || '').trim());
  const res = await api.post(`/api/agent-task-links/${safeTaskId}/dispatch`, payload, getAuthHeaders());
  return res.data || {};
};

export const updateAgentEntitlementsDev = async (payload = {}) => {
  const res = await api.patch('/api/agent/entitlements/dev', payload, getAuthHeaders());
  return res.data || { entitlements: {} };
};

export const listPersonalAgents = async () => {
  const res = await api.get('/api/agents/personal', getAuthHeaders());
  return Array.isArray(res.data) ? res.data : [];
};

export const createPersonalAgent = async (payload = {}) => {
  const res = await api.post('/api/agents/personal', payload, getAuthHeaders());
  return res.data || {};
};

export const updatePersonalAgent = async (agentId, payload = {}) => {
  const safeId = encodeURIComponent(String(agentId || '').trim());
  const res = await api.patch(`/api/agents/personal/${safeId}`, payload, getAuthHeaders());
  return res.data || {};
};

export const rotatePersonalAgentKey = async (agentId) => {
  const safeId = encodeURIComponent(String(agentId || '').trim());
  const res = await api.post(`/api/agents/personal/${safeId}/rotate-key`, {}, getAuthHeaders());
  return res.data || {};
};

export const disablePersonalAgent = async (agentId) => {
  const safeId = encodeURIComponent(String(agentId || '').trim());
  const res = await api.delete(`/api/agents/personal/${safeId}`, getAuthHeaders());
  return res.data || {};
};

export const listAgentTokens = async () => {
  const res = await api.get('/api/agent-tokens', getAuthHeaders());
  return res.data || { tokens: [] };
};

export const listAgentTokenActions = async (tokenId, { limit = 50 } = {}) => {
  const safeId = encodeURIComponent(String(tokenId || '').trim());
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  const suffix = params.toString();
  const res = await api.get(`/api/agent-tokens/${safeId}/actions${suffix ? `?${suffix}` : ''}`, getAuthHeaders());
  return res.data || { actions: [], counts: { today: 0, week: 0 } };
};

export const undoAgentTokenAction = async (action = {}) => {
  const undoPath = String(action?.undoPath || action?.metadata?.undoPath || '').trim();
  if (!undoPath || !undoPath.startsWith('/api/wiki/')) {
    throw new Error('This action cannot be undone from Settings.');
  }
  const res = await api.post(undoPath, {}, getAuthHeaders());
  return res.data || {};
};

export const createAgentToken = async (payload = {}) => {
  const res = await api.post('/api/agent-tokens', payload, getAuthHeaders());
  return res.data || {};
};

export const revokeAgentToken = async (tokenId) => {
  const safeId = encodeURIComponent(String(tokenId || '').trim());
  const res = await api.post(`/api/agent-tokens/${safeId}/revoke`, {}, getAuthHeaders());
  return res.data || {};
};

export const deleteAgentToken = async (tokenId) => {
  const safeId = encodeURIComponent(String(tokenId || '').trim());
  const res = await api.delete(`/api/agent-tokens/${safeId}`, getAuthHeaders());
  return res.data || {};
};

export const createAgentHandoff = async (payload = {}) => {
  const res = await api.post('/api/agent/protocol/handoffs', payload, getAuthHeaders());
  return res.data || {};
};

export const listAgentStructureProposals = async ({
  threadId = '',
  runId = '',
  status = 'all',
  scope = '',
  scopeRef = '',
  limit = 40
} = {}) => {
  const params = new URLSearchParams();
  if (threadId) params.set('threadId', String(threadId).trim());
  if (runId) params.set('runId', String(runId).trim());
  if (status) params.set('status', String(status).trim());
  if (scope) params.set('scope', String(scope).trim());
  if (scopeRef) params.set('scopeRef', String(scopeRef).trim());
  if (limit) params.set('limit', String(limit));
  const suffix = params.toString();
  const res = await api.get(`/api/agent/structure-proposals${suffix ? `?${suffix}` : ''}`, getAuthHeaders());
  return res.data || { proposals: [] };
};

export const updateAgentStructureProposal = async (structureProposalId, payload = {}) => {
  const safeId = encodeURIComponent(String(structureProposalId || '').trim());
  const res = await api.patch(`/api/agent/structure-proposals/${safeId}`, payload, getAuthHeaders());
  return res.data || {};
};

export const applyAgentStructureProposal = async (structureProposalId) => {
  const safeId = encodeURIComponent(String(structureProposalId || '').trim());
  const res = await api.post(`/api/agent/structure-proposals/${safeId}/apply`, {}, getAuthHeaders());
  return res.data || {};
};

export const rejectAgentStructureProposal = async (structureProposalId) => {
  const safeId = encodeURIComponent(String(structureProposalId || '').trim());
  const res = await api.post(`/api/agent/structure-proposals/${safeId}/reject`, {}, getAuthHeaders());
  return res.data || {};
};

export const rollbackAgentStructureProposal = async (structureProposalId) => {
  const safeId = encodeURIComponent(String(structureProposalId || '').trim());
  const res = await api.post(`/api/agent/structure-proposals/${safeId}/rollback`, {}, getAuthHeaders());
  return res.data || {};
};

export const getAgentThread = async (threadId) => {
  const safeId = encodeURIComponent(String(threadId || '').trim());
  const res = await api.get(`/api/agent/threads/${safeId}`, getAuthHeaders());
  return res.data || {};
};

export const listAgentHandoffs = async ({
  handoffId = '',
  status = 'all',
  taskType = '',
  requestedActorType = '',
  requestedActorId = '',
  mine = false,
  actorType = '',
  actorId = '',
  limit = 40
} = {}) => {
  const params = new URLSearchParams();
  if (handoffId) params.set('handoffId', String(handoffId).trim());
  if (status) params.set('status', String(status).trim());
  if (taskType) params.set('taskType', String(taskType).trim());
  if (requestedActorType) params.set('requestedActorType', String(requestedActorType).trim());
  if (requestedActorId) params.set('requestedActorId', String(requestedActorId).trim());
  if (mine) params.set('mine', 'true');
  if (actorType) params.set('actorType', String(actorType).trim());
  if (actorId) params.set('actorId', String(actorId).trim());
  if (limit) params.set('limit', String(limit));
  const suffix = params.toString();
  const res = await api.get(`/api/agent/protocol/handoffs${suffix ? `?${suffix}` : ''}`, getAuthHeaders());
  return res.data || { handoffs: [] };
};

export const claimAgentHandoff = async (handoffId, payload = {}) => {
  const safeId = encodeURIComponent(String(handoffId || '').trim());
  const res = await api.post(`/api/agent/protocol/handoffs/${safeId}/claim`, payload, getAuthHeaders());
  return res.data || {};
};

export const completeAgentHandoff = async (handoffId, payload = {}) => {
  const safeId = encodeURIComponent(String(handoffId || '').trim());
  const res = await api.post(`/api/agent/protocol/handoffs/${safeId}/complete`, payload, getAuthHeaders());
  return res.data || {};
};

export const rejectAgentHandoff = async (handoffId, payload = {}) => {
  const safeId = encodeURIComponent(String(handoffId || '').trim());
  const res = await api.post(`/api/agent/protocol/handoffs/${safeId}/reject`, payload, getAuthHeaders());
  return res.data || {};
};

export const cancelAgentHandoff = async (handoffId, payload = {}) => {
  const safeId = encodeURIComponent(String(handoffId || '').trim());
  const res = await api.post(`/api/agent/protocol/handoffs/${safeId}/cancel`, payload, getAuthHeaders());
  return res.data || {};
};

export const ensureAgentHandoffThread = async (handoffId) => {
  const safeId = encodeURIComponent(String(handoffId || '').trim());
  const res = await api.post(`/api/agent/protocol/handoffs/${safeId}/thread`, {}, getAuthHeaders());
  return res.data || {};
};

export const createAutoAgentHandoff = async (payload = {}) => {
  const res = await api.post('/api/agent/protocol/handoffs/auto', payload, getAuthHeaders());
  return res.data || {};
};

export const getAgentProtocolPolicy = async () => {
  const res = await api.get('/api/agent/protocol/policy', getAuthHeaders());
  return res.data || { policy: {} };
};

export const updateAgentProtocolPolicy = async (payload = {}) => {
  const res = await api.patch('/api/agent/protocol/policy', payload, getAuthHeaders());
  return res.data || { policy: {} };
};

export const createAgentBridgeToken = async (payload = {}) => {
  const res = await api.post('/api/agent/protocol/bridge/token', payload, getAuthHeaders());
  return res.data || {};
};

export const getAgentBridgeManifest = async (bridgeToken) => {
  const safeBridgeToken = String(bridgeToken || '').trim();
  const res = await api.get('/api/agent/protocol/bridge/manifest', {
    headers: {
      Authorization: `Bearer ${safeBridgeToken}`
    }
  });
  return res.data || {};
};

export const executeAgentBridgeMcp = async (bridgeToken, {
  method,
  params = {},
  id = 1
} = {}) => {
  const safeBridgeToken = String(bridgeToken || '').trim();
  const res = await api.post('/api/agent/protocol/bridge/mcp', {
    jsonrpc: '2.0',
    id,
    method,
    params
  }, {
    headers: {
      Authorization: `Bearer ${safeBridgeToken}`
    }
  });
  return res.data || {};
};

export const listAgentProtocolApprovals = async ({
  status = 'pending',
  limit = 30,
  threadId = '',
  handoffId = '',
  op = ''
} = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', String(status).trim());
  if (limit) params.set('limit', String(limit));
  if (threadId) params.set('threadId', String(threadId).trim());
  if (handoffId) params.set('handoffId', String(handoffId).trim());
  if (op) params.set('op', String(op).trim());
  const suffix = params.toString();
  const res = await api.get(`/api/agent/protocol/approvals${suffix ? `?${suffix}` : ''}`, getAuthHeaders());
  return res.data || { approvals: [] };
};

export const approveAgentProtocolApproval = async (approvalId, payload = {}) => {
  const safeId = encodeURIComponent(String(approvalId || '').trim());
  const res = await api.post(`/api/agent/protocol/approvals/${safeId}/approve`, payload, getAuthHeaders());
  return res.data || {};
};

export const rejectAgentProtocolApproval = async (approvalId, payload = {}) => {
  const safeId = encodeURIComponent(String(approvalId || '').trim());
  const res = await api.post(`/api/agent/protocol/approvals/${safeId}/reject`, payload, getAuthHeaders());
  return res.data || {};
};

// Notion agent fetch — POSTs the Notion fetch tool and returns the
// structured summary { status, fetched, created, updated, skipped, failed,
// errors, summary }. Per the design, this is user-triggered only.
export const fetchNotionPagesViaAgent = async ({ connectionId, limit } = {}) => {
  const res = await api.post(
    '/api/agent/tools/notion-fetch',
    { connectionId, limit },
    getAuthHeaders()
  );
  return res.data || {};
};
